<template>
	<div class="crRoot">
		<h1 class="pageHeading">Code Review</h1>
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }" class="active" aria-current="page">Report</router-link>
		</nav>

		<div class="card">
			<form class="row" @submit.prevent="apply">
				<label for="crrFrom">From</label>
				<input id="crrFrom" type="date" v-model="from" required />
				<label for="crrUntil">Until (exclusive)</label>
				<input id="crrUntil" type="date" v-model="until" required />
				<button type="submit" :disabled="loading">Load</button>
				<span v-if="loading" class="loadingInline"><ScaleLoader /> Loading…</span>
			</form>
			<div class="row">
				<span class="hintText">Quick:</span>
				<button type="button" @click="quick('last7')">Last 7 days</button>
				<button type="button" @click="quick('last30')">Last 30 days</button>
				<button type="button" @click="quick('thisMonth')">This month</button>
				<button type="button" @click="quick('lastMonth')">Last month</button>
				<a v-if="validRange" :href="htmlUrl" target="_blank" rel="noopener">Open the HTML version</a>
			</div>
		</div>

		<div v-if="error" class="errorBox">{{error}}</div>

		<template v-if="report">
			<div class="card">
				<div class="row">
					<b>{{report.fromDate}} to {{report.lastDate}}</b>
					<span class="hintText">{{report.days}} day{{report.days === 1 ? '' : 's'}}, server time zone {{report.timeZone}}</span>
				</div>
				<div v-if="!report.hasData" class="hintText" role="status">No pushes were reviewed and no findings were created or resolved in this period.</div>
			</div>

			<div v-if="report.hasData" class="card tableCard">
				<h2 class="secondaryHeading">By repository</h2>
				<table class="dense">
					<thead>
						<tr>
							<th scope="col">Repository</th>
							<th scope="col" class="num">Pushes</th>
							<th scope="col" class="num">Reviewed</th>
							<th scope="col" class="num">Failed</th>
							<th scope="col" class="num">Safety</th>
							<th scope="col" class="num">Pending</th>
							<th scope="col" class="num">Commits reviewed</th>
							<th scope="col" class="num">Pushes notified</th>
							<th scope="col" class="num">New C/H/M/L</th>
							<th scope="col" class="num">Resolved</th>
							<th scope="col" class="num">Open at end</th>
							<th scope="col" class="num">Est. cost</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="s in overviewRows" :key="s.repo || '(total)'" :class="{ totalRow: !s.repo }">
							<td>
								<button v-if="s.repo" type="button" class="linkButton" :title="s.repo" @click="selectedRepo = s.repo">{{s.repoDisplayName}}</button>
								<template v-else>Total</template>
							</td>
							<td class="num">{{s.coverage.pushes}}</td>
							<td class="num">{{s.coverage.done + s.coverage.safety}}</td>
							<td class="num">{{s.coverage.failed}}</td>
							<td class="num">{{s.coverage.safety}}</td>
							<td class="num">{{s.coverage.inProgress}}</td>
							<td class="num">{{s.coverage.commitsReviewed}}</td>
							<td class="num">{{s.coverage.pushesNotified}}</td>
							<td class="num nowrap">{{s.newFindings.critical}} / {{s.newFindings.high}} / {{s.newFindings.medium}} / {{s.newFindings.low}}</td>
							<td class="num">{{s.resolved.total}}</td>
							<td class="num">{{s.openAtEnd.total}}</td>
							<td class="num">{{formatCost(s.usage.costUsd)}}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div class="card">
				<div class="row">
					<label for="crrRepo">Details for</label>
					<select id="crrRepo" v-model="selectedRepo">
						<option value="">All repositories</option>
						<option v-for="s in report.repos" :key="s.repo" :value="s.repo">{{repoLabel(s)}}</option>
					</select>
				</div>
			</div>

			<div v-if="section" class="grid">
				<div class="card">
					<h2 class="secondaryHeading">New findings</h2>
					<table class="dense compact">
						<tbody>
							<tr v-for="r in severityRows(section.newFindings)" :key="r.name">
								<td><span class="badge" :class="severityClass(r.name)">{{r.name}}</span></td>
								<td class="num">{{r.count}}</td>
								<td><span class="bar" aria-hidden="true"><span :class="'bar' + r.name" :style="{ width: r.pct + '%' }"></span></span></td>
							</tr>
							<tr class="totalRow"><td>Total</td><td class="num">{{section.newFindings.total}}</td><td></td></tr>
						</tbody>
					</table>
					<div class="hintText">Notified: {{section.newNotified}} ({{section.newFlaggedToNotify}} marked to notify)</div>
				</div>

				<div class="card">
					<h2 class="secondaryHeading">Resolved findings</h2>
					<table class="dense compact">
						<tbody>
							<tr v-for="r in severityRows(section.resolved)" :key="r.name">
								<td><span class="badge" :class="severityClass(r.name)">{{r.name}}</span></td>
								<td class="num">{{r.count}}</td>
								<td><span class="bar" aria-hidden="true"><span :class="'bar' + r.name" :style="{ width: r.pct + '%' }"></span></span></td>
							</tr>
							<tr class="totalRow"><td>Total</td><td class="num">{{section.resolved.total}}</td><td></td></tr>
						</tbody>
					</table>
				</div>

				<div class="card">
					<h2 class="secondaryHeading">Open at period end</h2>
					<table class="dense compact">
						<tbody>
							<tr v-for="r in severityRows(section.openAtEnd)" :key="r.name">
								<td><span class="badge" :class="severityClass(r.name)">{{r.name}}</span></td>
								<td class="num">{{r.count}}</td>
								<td><span class="bar" aria-hidden="true"><span :class="'bar' + r.name" :style="{ width: r.pct + '%' }"></span></span></td>
							</tr>
							<tr class="totalRow"><td>Total</td><td class="num">{{section.openAtEnd.total}}</td><td></td></tr>
						</tbody>
					</table>
				</div>

				<div class="card">
					<h2 class="secondaryHeading">Resolutions</h2>
					<table class="dense compact">
						<tbody>
							<tr v-for="r in resolutionRows" :key="r.key">
								<td :class="{ indent: r.indent }">{{r.name}}</td>
								<td class="num">{{r.count}}</td>
								<td><span class="bar" aria-hidden="true"><span :style="{ width: r.pct + '%' }"></span></span></td>
							</tr>
							<tr class="totalRow"><td>Total</td><td class="num">{{section.resolvedByResolution.total}}</td><td></td></tr>
						</tbody>
					</table>
					<div class="hintText">Time to resolution: median {{formatDays(section.timeToResolution.medianDays)}}, 90th percentile {{formatDays(section.timeToResolution.p90Days)}} ({{section.timeToResolution.count}} resolved)</div>
				</div>

				<div class="card">
					<h2 class="secondaryHeading">Review coverage</h2>
					<table class="dense compact">
						<tbody>
							<tr><td>Pushes</td><td class="num">{{section.coverage.pushes}}</td></tr>
							<tr><td class="indent">Done</td><td class="num">{{section.coverage.done}}</td></tr>
							<tr><td class="indent">Failed</td><td class="num">{{section.coverage.failed}}</td></tr>
							<tr><td class="indent">Safety</td><td class="num">{{section.coverage.safety}}</td></tr>
							<tr><td class="indent">Skipped</td><td class="num">{{section.coverage.skipped}}</td></tr>
							<tr><td class="indent">Still pending</td><td class="num">{{section.coverage.inProgress}}</td></tr>
							<tr><td>Commits reviewed</td><td class="num">{{section.coverage.commitsReviewed}} of {{section.coverage.commits}}</td></tr>
							<tr><td>Pushes with a notifying finding</td><td class="num">{{section.coverage.pushesWithNotifyingFinding}}</td></tr>
							<tr><td>Pushes notified</td><td class="num">{{section.coverage.pushesNotified}}</td></tr>
						</tbody>
					</table>
				</div>

				<div class="card">
					<h2 class="secondaryHeading">Triage rules applied</h2>
					<table v-if="section.rules.length" class="dense compact">
						<tbody>
							<tr v-for="r in ruleRows" :key="r.name">
								<td>{{r.name}}</td>
								<td class="num">{{r.count}}</td>
								<td><span class="bar" aria-hidden="true"><span :style="{ width: r.pct + '%' }"></span></span></td>
							</tr>
						</tbody>
					</table>
					<div v-else class="hintText">None.</div>
				</div>

				<div v-if="section.fixes && (section.fixes.batches || section.fixes.batchesPushed)" class="card">
					<h2 class="secondaryHeading">Autonomous fixes</h2>
					<table class="dense compact">
						<tbody>
							<tr><td>Fix batches created</td><td class="num">{{section.fixes.batches}}</td></tr>
							<tr v-for="s in section.fixes.byStatus" :key="s.name"><td class="indent">{{label(s.name)}}</td><td class="num">{{s.count}}</td></tr>
							<tr><td>Findings in them</td><td class="num">{{section.fixes.findingsInBatches}}</td></tr>
							<tr><td>Batches pushed</td><td class="num">{{section.fixes.batchesPushed}}</td></tr>
							<tr><td>Commits pushed</td><td class="num">{{section.fixes.commitsPushed}}</td></tr>
							<tr><td>Findings fixed and pushed</td><td class="num">{{section.fixes.findingsFixedAndPushed}}</td></tr>
						</tbody>
					</table>
				</div>
			</div>

			<div v-if="section" class="card tableCard">
				<h2 class="secondaryHeading">Agent usage</h2>
				<div v-if="!section.usage.runs" class="hintText">No agent runs started in this period.</div>
				<template v-else>
					<p class="usageLine">
						{{section.usage.runs}} run{{section.usage.runs === 1 ? '' : 's'}}, {{formatDurationMs(section.usage.durationMs)}} of agent time,
						<b>{{formatCost(section.usage.costUsd)}}</b> {{report.costLabel}}.
						<template v-if="section.usage.pushesWithRuns">
							Per push ({{section.usage.pushesWithRuns}}): {{formatCost(section.usage.averageCostPerPushUsd)}} and {{formatDurationMs(section.usage.averageDurationPerPushMs)}}, including retries.
						</template>
						<template v-if="section.usage.runsWithoutCost">
							{{section.usage.runsWithoutCost}} finished run{{section.usage.runsWithoutCost === 1 ? ' has' : 's have'}} no cost recorded.
						</template>
					</p>
					<table class="dense usageTable">
						<thead>
							<tr>
								<th scope="col">Kind</th>
								<th scope="col" class="num">Runs</th>
								<th scope="col" class="num">Ok</th>
								<th scope="col" class="num">Failed</th>
								<th scope="col" class="num">Safety</th>
								<th scope="col" class="num">Running</th>
								<th scope="col" class="num">Agent time</th>
								<th scope="col" class="num">Est. cost</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="k in section.usage.byKind" :key="k.kind">
								<td>{{label(k.kind)}}</td>
								<td class="num">{{k.runs}}</td>
								<td class="num">{{k.ok}}</td>
								<td class="num">{{k.failed}}</td>
								<td class="num">{{k.safety}}</td>
								<td class="num">{{k.running}}</td>
								<td class="num">{{formatDurationMs(k.durationMs)}}</td>
								<td class="num">{{formatCost(k.costUsd)}}</td>
							</tr>
						</tbody>
					</table>
					<table class="dense usageTable">
						<thead>
							<tr>
								<th scope="col">Model</th>
								<th scope="col" class="num">Runs</th>
								<th scope="col" class="num">Input</th>
								<th scope="col" class="num">Output</th>
								<th scope="col" class="num">Cache read</th>
								<th scope="col" class="num">Cache write</th>
								<th scope="col" class="num">Total tokens</th>
								<th scope="col" class="num">Est. cost</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="m in section.usage.byModel" :key="m.model">
								<td class="mono">{{m.model}}</td>
								<td class="num">{{m.runs}}</td>
								<td class="num">{{formatCount(m.tokens.input)}}</td>
								<td class="num">{{formatCount(m.tokens.output)}}</td>
								<td class="num">{{formatCount(m.tokens.cacheRead)}}</td>
								<td class="num">{{formatCount(m.tokens.cacheWrite)}}</td>
								<td class="num">{{formatCount(m.tokens.total)}}</td>
								<td class="num">{{formatCost(m.costUsd)}}</td>
							</tr>
							<tr class="totalRow">
								<td>Total</td>
								<td></td>
								<td class="num">{{formatCount(section.usage.tokens.input)}}</td>
								<td class="num">{{formatCount(section.usage.tokens.output)}}</td>
								<td class="num">{{formatCount(section.usage.tokens.cacheRead)}}</td>
								<td class="num">{{formatCount(section.usage.tokens.cacheWrite)}}</td>
								<td class="num">{{formatCount(section.usage.tokens.total)}}</td>
								<td class="num">{{formatCost(section.usage.costUsd)}}</td>
							</tr>
						</tbody>
					</table>
				</template>
			</div>

			<div class="card">
				<ul class="notes hintText">
					<li v-for="(n, i) in report.notes" :key="i">{{n}}</li>
				</ul>
			</div>
		</template>
	</div>
</template>

<script>
	import { GetReport, GetReportHtmlUrl } from 'appRoot/api/CodeReviewData';
	import { severities, rejectReasons, label, severityClass, formatCost, formatCount, formatDurationMs } from './codeReviewFormat';

	const datePattern = /^\d{4}-\d{2}-\d{2}$/;

	function pad(n)
	{
		return n < 10 ? '0' + n : '' + n;
	}
	/** Local "YYYY-MM-DD". */
	function toDateString(d)
	{
		return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
	}
	function addDays(d, days)
	{
		return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
	}
	function today()
	{
		let now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), now.getDate());
	}
	/** [from, until) for a quick range name; until is exclusive, so "last 7 days" ends tomorrow. */
	function quickRange(name)
	{
		let t = today();
		switch (name)
		{
			case 'last7':
				return { from: toDateString(addDays(t, -6)), until: toDateString(addDays(t, 1)) };
			case 'thisMonth':
				return { from: toDateString(new Date(t.getFullYear(), t.getMonth(), 1)), until: toDateString(new Date(t.getFullYear(), t.getMonth() + 1, 1)) };
			case 'lastMonth':
				return { from: toDateString(new Date(t.getFullYear(), t.getMonth() - 1, 1)), until: toDateString(new Date(t.getFullYear(), t.getMonth(), 1)) };
			default:
				return { from: toDateString(addDays(t, -29)), until: toDateString(addDays(t, 1)) };
		}
	}
	function pct(value, max)
	{
		return max > 0 ? Math.round(100 * value / max) : 0;
	}

	export default {
		data()
		{
			return {
				from: '',
				until: '',
				report: null,
				selectedRepo: '',
				loading: false,
				error: null,
				loadToken: 0,
				/** "from|until" of the last load, so a query change that matches it does not reload. */
				loadedRange: ''
			};
		},
		created()
		{
			this.applyQuery();
			this.load();
		},
		computed:
		{
			validRange()
			{
				return datePattern.test(this.from) && datePattern.test(this.until) && this.from < this.until;
			},
			htmlUrl()
			{
				return GetReportHtmlUrl(this.from, this.until);
			},
			section()
			{
				if (!this.report)
					return null;
				if (this.selectedRepo)
				{
					let s = this.report.repos.find(r => r.repo === this.selectedRepo);
					if (s)
						return s;
				}
				return this.report.total;
			},
			overviewRows()
			{
				if (!this.report)
					return [];
				return this.report.repos.concat([this.report.total]);
			},
			resolutionRows()
			{
				let c = this.section.resolvedByResolution;
				let rows = [
					{ key: 'fixed', name: 'Fixed', count: c.fixedCount },
					{ key: 'alreadyFixed', name: 'Already fixed', count: c.alreadyFixed },
					{ key: 'rejected', name: 'Rejected', count: c.rejected }
				];
				for (let r of c.rejectedByReason)
					rows.push({ key: 'reason' + r.name, name: this.rejectReasonLabel(r.name), count: r.count, indent: true });
				rows.push({ key: 'duplicate', name: 'Duplicate / superseded', count: c.duplicateOrSuperseded });
				let max = Math.max(0, ...rows.map(r => r.count));
				for (let r of rows)
					r.pct = pct(r.count, max);
				return rows;
			},
			ruleRows()
			{
				let rules = this.section.rules;
				let max = Math.max(0, ...rules.map(r => r.count));
				return rules.map(r => ({ name: r.name, count: r.count, pct: pct(r.count, max) }));
			}
		},
		watch:
		{
			'$route.query'()
			{
				if (this.$route.name !== 'codeReviewReport')
					return;
				if (this.applyQuery())
					this.load();
			}
		},
		methods:
		{
			label,
			severityClass,
			formatCost,
			formatCount,
			formatDurationMs,
			/** Takes from/until from the query string (default: the last 30 days).  Returns true if they changed. */
			applyQuery()
			{
				let q = this.$route.query || {};
				let range;
				if (typeof q.from === 'string' && typeof q.until === 'string' && datePattern.test(q.from) && datePattern.test(q.until) && q.from < q.until)
					range = { from: q.from, until: q.until };
				else
					range = quickRange('last30');
				this.from = range.from;
				this.until = range.until;
				return range.from + '|' + range.until !== this.loadedRange;
			},
			quick(name)
			{
				let range = quickRange(name);
				this.from = range.from;
				this.until = range.until;
				this.apply();
			},
			apply()
			{
				if (!this.validRange)
				{
					this.error = 'Choose a From date before the Until date.';
					return;
				}
				let q = this.$route.query || {};
				if (q.from === this.from && q.until === this.until)
					this.load();
				else
					this.$router.replace({ name: 'codeReviewReport', query: { from: this.from, until: this.until } });
			},
			severityRows(counts)
			{
				let values = severities.map(s => counts[s.toLowerCase()]);
				let max = Math.max(0, ...values);
				return severities.map((s, i) => ({ name: s, count: values[i], pct: pct(values[i], max) }));
			},
			rejectReasonLabel(name)
			{
				if (name === 'None')
					return 'No reason given';
				let r = rejectReasons.find(x => x.value === name);
				return r ? r.label : name;
			},
			repoLabel(s)
			{
				return s.repoDisplayName === s.repo ? s.repo : s.repoDisplayName + ' (' + s.repo + ')';
			},
			formatDays(days)
			{
				if (days === null || days === undefined)
					return '—';
				let s = days.toFixed(1);
				return s + (s === '1.0' ? ' day' : ' days');
			},
			async load()
			{
				let token = ++this.loadToken;
				this.loadedRange = this.from + '|' + this.until;
				this.loading = true;
				this.error = null;
				try
				{
					let data = await GetReport(this.from, this.until);
					if (token !== this.loadToken)
						return;
					if (data.success)
					{
						this.report = data.report;
						if (this.selectedRepo && !this.report.repos.some(r => r.repo === this.selectedRepo))
							this.selectedRepo = '';
					}
					else
					{
						this.report = null;
						this.error = data.error;
					}
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
						this.loading = false;
				}
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.tableCard
	{
		overflow-x: auto;
	}

	.grid
	{
		display: flex;
		flex-wrap: wrap;
		gap: 0 10px;
		align-items: flex-start;
	}

		.grid > .card
		{
			flex: 1 1 260px;
			min-width: 240px;
		}

	.num
	{
		text-align: right !important;
		font-variant-numeric: tabular-nums;
		white-space: nowrap;
	}

	.dense.compact
	{
		width: auto;
		min-width: 220px;
	}

	.dense tr.totalRow td
	{
		font-weight: bold;
		border-top: 2px solid #C8C8C8;
	}

	.indent
	{
		padding-left: 22px !important;
	}

	.bar
	{
		display: inline-block;
		width: 110px;
		height: 10px;
		background-color: #EDF2F7;
		border-radius: 5px;
		overflow: hidden;
		vertical-align: middle;
	}

		.bar > span
		{
			display: block;
			height: 100%;
			background-color: #1A5FB4;
		}

			.bar > span.barCritical
			{
				background-color: #8B0000;
			}

			.bar > span.barHigh
			{
				background-color: #B33C00;
			}

			.bar > span.barMedium
			{
				background-color: #D9A400;
			}

			.bar > span.barLow
			{
				background-color: #9AA5B1;
			}

	.usageLine
	{
		margin: 0 0 8px 0;
	}

	.usageTable
	{
		width: auto;
		margin-bottom: 10px;
	}

	.notes
	{
		margin: 0;
		padding-left: 1.2em;
	}

	input[type=date]
	{
		font: inherit;
	}
</style>
